import { useEffect, useRef, useState } from 'react';
import { useCalculator } from '../../engine/state/CalculatorProvider';
import type { HsaSavingsResult } from './calculate';

/** Matches .hsab-calc-savings__bar's `transition: height 0.25s linear`. */
const HEIGHT_TRANSITION_MS = 250;

/**
 * Ports hsa-savings-calc.js's DOLLAR_POINT_SETS y-axis-scale logic verbatim
 * (every bracket uses 5 points in the legacy table, so numPoints is fixed at
 * 5 in practice — kept explicit rather than hardcoded for fidelity to the
 * source).
 */
const DOLLAR_POINT_SETS: ReadonlyArray<readonly [number, number]> = [
  [100, 5], [200, 5], [300, 5], [400, 5], [500, 5], [600, 5], [700, 5], [800, 5], [900, 5], [1000, 5],
  [2000, 5], [3000, 5], [4000, 5], [5000, 5], [6000, 5], [7000, 5], [8000, 5], [9000, 5],
  [10000, 5], [20000, 5], [30000, 5], [40000, 5], [50000, 5], [60000, 5], [70000, 5], [80000, 5], [90000, 5],
  [100000, 5], [200000, 5], [300000, 5], [400000, 5], [500000, 5],
];

/**
 * The bar's drawable height in px: .hsab-calc-savings__chart's declared
 * 347px (bumped from 280 to 340 2026-09-21 — read as too squished; 340 to
 * 347 2026-10-02 — 7px of air added above the year numbers, see below),
 * minus the 1px border-bottom on __x-axis, minus the bar's own bottom
 * reserve (see styles.scss — the bar is bottom: calc(1.75rem + 7px), not
 * flush with the container floor): 1.75rem for __bar-year itself, plus a
 * real 7px gap above it so the numbers don't touch/overlap the bars (Brad,
 * 2026-10-02 — measured -3px to 0px on real local Magnolia; 7px lands at
 * ~4px of real visible gap there, not a clean 4 -- the pre-existing
 * overlap had to be undone first). That whole reserve grew, not
 * BAR_DRAW_HEIGHT_PX itself: the 7px came from growing __chart's own
 * height (above) to make room, not from shrinking the bars' drawable
 * area. A couple of px of margin on top of that. Keep this in sync with
 * __chart's height if that changes again.
 *
 * Found live (2026-09-08, Brad, 30-year case): the tallest (structurally
 * last) bar rendered ~20px shorter than every value on either side of it,
 * breaking the chart's monotonic shape at the top — even when its
 * requested height was comfortably under the container's real available
 * space, ruling out simple overflow-clamping. Reproduced identically with
 * CSS transitions fully disabled and after a hard reload, so not a
 * transition artifact either. Root cause: the bar's height was being set
 * on a flex item inside a column flex container (each bar-container was
 * `display:flex; flex-direction:column; height:100%`) — some browsers'
 * flex-shrink resolution for the structurally-last item in a wide,
 * many-sibling row is unreliable at that specific position, independent
 * of the actual size being requested. Rather than keep chasing the exact
 * shrink-algorithm mechanism, the bar is now absolutely positioned within
 * a `position: relative` bar-container instead of flex-height-sized —
 * that removes it from flex height computation entirely, which is the
 * standard, more robust way to build a bar chart in CSS regardless of
 * whether this exact bug recurs.
 */
// 1.75rem is 17.5px at the site's 10px root font-size (not 28px), so the
// plot area is 347 - 1 (border) - 17.5 - 7 (year-number air gap, 2026-10-02)
// = 321.5px; 321 keeps the top tick flush.
const BAR_DRAW_HEIGHT_PX = 321;

/** Narrowest column a centered final-bar label ("$123,456.78") fits in. */
const FINAL_LABEL_MIN_COLUMN_PX = 110;

/**
 * Y-axis labels and gridlines are placed at their true value: label i (of
 * numPoints) sits i/numPoints of the way up the same drawable height bars use.
 * Previously the labels were spread with space-between (top label at the top,
 * lowest label on the floor), which put e.g. "4k" at zero and made a bar of
 * $11.9k poke above the "12k" line (Brad, 2026-09-23).
 */
function tickBottom(i: number, numPoints: number, extraPx: number): string {
  // labels are ordered top-first, so index 0 is the highest tick
  const frac = (numPoints - i) / numPoints;
  // The +7px matches __bar's own bottom reserve (styles.scss) -- ticks sit
  // on the same baseline the bars grow from.
  return `calc(1.75rem + 7px + ${extraPx}px + ${(frac * BAR_DRAW_HEIGHT_PX).toFixed(2)}px)`;
}

function barHeightPx(amt: number, topValue: number): number {
  if (topValue === 0) return 0;
  const pct = Math.max(0, amt) / topValue;
  return Math.min(BAR_DRAW_HEIGHT_PX, Math.round(pct * BAR_DRAW_HEIGHT_PX));
}

function resolveTopValue(totalSavings: number): number {
  for (const [top] of DOLLAR_POINT_SETS) {
    if (totalSavings <= top) return top;
  }
  return DOLLAR_POINT_SETS[DOLLAR_POINT_SETS.length - 1][0];
}

function formatAxisLabel(value: number): string {
  // "$30k": the axis is dollar values, like the totals below it. Prod's chart
  // had "30 k" with no "$" and no axis titles; Brad restored both on
  // 2026-10-19 (first added 2026-09-28, reverted 2026-10-09 pending
  // legal/compliance).
  if (value >= 1000) {
    let s = (value * 0.001).toFixed(1);
    if (s.endsWith('.0')) s = s.slice(0, -2);
    return `$${s}k`;
  }
  return `$${value}`;
}

/** Axis titles shown on every layout (the wording Spotlight already used). */
const Y_AXIS_TITLE = 'Account balance';
const X_AXIS_TITLE = 'Years';

function yAxisLabels(topValue: number, numPoints: number): string[] {
  const increment = topValue / numPoints;
  const labels: string[] = [];
  for (let i = 1; i <= numPoints; i++) {
    labels.push(formatAxisLabel(increment * i));
  }
  return labels.reverse();
}

/**
 * At high year counts, a label under every single bar (two digits, a very
 * narrow flex share each) becomes illegible and visually collides — thin
 * them out, always keeping the first and last year labeled so the range is
 * still readable at a glance.
 */
function shouldShowYearLabel(index: number, total: number, pitchPx: number | null): boolean {
  // Also thin by the real column pitch (column + gap): on a phone, 30 bars
  // leave ~9px each and even every-other-year labels collided (2026-09-28).
  // Needs ~24px per shown label for a 2-digit year.
  const widthStep = pitchPx && pitchPx > 0 ? Math.ceil(24 / pitchPx) : 1;
  const step = Math.max(total <= 15 ? 1 : Math.ceil(total / 15), widthStep);
  if (step <= 1) return true;
  // First and last always labeled; skip a stepped label that would crowd either.
  if (index === 0 || index === total - 1) return true;
  if ((index + 1) % step !== 0) return false;
  return index >= step && total - 1 - index >= step;
}

/**
 * Bars get wider the fewer of them there are, instead of staying a flat
 * 28px regardless of how much room each column actually has (Brad,
 * 2026-09-22). 320/total is just a curve that feels right empirically, not
 * derived from anything — capped at 90px so 1-2 years doesn't produce an
 * absurdly fat single bar, floored at 28px (the old fixed value) so
 * high-count behavior, already fine per Brad, is untouched: the floor only
 * matters below ~12 years, since past that each column is already narrower
 * than 28px on its own and this max-width was never the binding constraint.
 */
function maxBarWidthPx(total: number): number {
  if (total <= 0) return 28;
  return Math.max(28, Math.min(90, 320 / total));
}

/**
 * Prod's formatDollars (hsa-savings-calc.js) verbatim, with its "$" prefix:
 * same cents rounding (toFixed), and a negative reads "$-3,090.00" as on
 * prod (2026-10-09; was toLocaleString's "-$3,090.00").
 */
export function formatDollarPrecise(n: number): string {
  return '$' + n.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
}

export function BarChart({
  result,
  spotlight = false,
}: {
  result: HsaSavingsResult;
  /**
   * Spotlight layout (Brad, 2026-09-28): the final-year bar is highlighted and
   * carries a permanent value label; every other bar keeps its hover tooltip.
   * Only the final bar is labeled because at high year counts the bars are too
   * narrow for per-bar labels.
   */
  spotlight?: boolean;
}) {
  const { options } = useCalculator();
  const topValue = resolveTopValue(result.totalSavings);
  const numPoints = DOLLAR_POINT_SETS.find(([top]) => result.totalSavings <= top)?.[1] ?? 5;
  const labels = yAxisLabels(topValue, numPoints);
  const amounts = result.compoundedSavingsByYear;
  const total = amounts.length;

  /**
   * Every other input change (contribution amount, tax rate, ...) keeps the
   * same number of bars, so React re-renders the same DOM nodes with a new
   * `height` and the CSS transition animates it — that's the "nice"
   * animation Brad meant. Adding/removing years changes the array length
   * instead, so React just mounts/unmounts bars outright; a CSS transition
   * has nothing to interpolate between on a fresh mount or an instant
   * unmount, so those changes visibly snapped with no animation at all
   * (found 2026-09-22, Brad).
   *
   * Fix: never let the rendered bar count jump directly from the old total
   * to the new one.
   * - Growing: mount the new bars right away, but starting at height 0
   *   (`enterFrom`) for exactly one frame, then flip them to their real
   *   height — the transition now has a "from" state to animate out of.
   * - Shrinking: keep the excess bars mounted (rendered with a forced
   *   target height of 0, so they visibly shrink away) for the same
   *   duration the CSS transition takes, then actually drop them from the
   *   DOM once they're already invisible.
   */
  const [displayCount, setDisplayCount] = useState(total);
  const [enterFrom, setEnterFrom] = useState(total);
  const prevTotalRef = useRef(total);
  const shrinkTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (total === prevTotalRef.current) return;
    window.clearTimeout(shrinkTimeoutRef.current);

    if (total > prevTotalRef.current) {
      setEnterFrom(prevTotalRef.current);
      setDisplayCount(total);
      const raf = requestAnimationFrame(() => setEnterFrom(total));
      prevTotalRef.current = total;
      return () => cancelAnimationFrame(raf);
    }

    setEnterFrom(total);
    shrinkTimeoutRef.current = window.setTimeout(() => setDisplayCount(total), HEIGHT_TRANSITION_MS + 20);
    prevTotalRef.current = total;
  }, [total]);

  useEffect(() => () => window.clearTimeout(shrinkTimeoutRef.current), []);

  const renderCount = Math.max(displayCount, total);
  const barMaxWidth = maxBarWidthPx(renderCount);

  /**
   * The vertical (height) animation above was the easy half — it's a
   * property React already re-renders as an explicit pixel value on the
   * *same* DOM node, so the CSS `transition: height` has two real states to
   * interpolate between. The horizontal layout is different: every bar's
   * column is sized by `flex: 1` sharing the row with however many
   * siblings currently exist, and that share is a *result* of the layout
   * algorithm, not a property value React ever sets directly — so when the
   * sibling count changes, the browser just recomputes and snaps to the
   * new share instantly. `transition: width` can't help unless something
   * actually gives each column an explicit `width` value to transition
   * between across renders, the same trick used for height. Measuring the
   * track and computing that width ourselves is that trick (found
   * 2026-09-22, Brad: bars "snap to the right" once a year is removed even
   * though the vertical shrink itself now animates correctly).
   */
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState<number | null>(null);
  const [gapPx, setGapPx] = useState(0);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    // Belt-and-suspenders against a ResizeObserver feedback loop (the real
    // cause of the runaway-growth bug was the missing min-width: 0 on
    // __x-axis, fixed in styles.scss) -- only push a new measurement
    // through when it's an actual, meaningful change, not sub-pixel
    // rounding noise from the last render.
    const measure = () => {
      // clientWidth includes the track's own padding-left (0.5rem), but the
      // bar columns are laid out inside the *content* box — sizing them from
      // clientWidth made the row overrun the chart's right edge by exactly
      // that padding (Brad, 2026-09-23: bars reached past the totals boxes'
      // edge around 676-725px). Subtract both paddings.
      const cs = getComputedStyle(el);
      const width = el.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      // A hidden chart (display:none, e.g. Spotlight's plan-type gate) measures
      // 0. Ignore that: bars would otherwise be sized to nothing and then
      // visibly widen sideways once the chart is revealed.
      if (width <= 0) return;
      setTrackWidth((prev) => (prev === null || Math.abs(prev - width) >= 1 ? width : prev));
      const gap = parseFloat(getComputedStyle(el).columnGap || '0') || 0;
      setGapPx((prev) => (Math.abs(prev - gap) >= 0.5 ? gap : prev));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Based on `total` (the settled target count), not `renderCount` (which
  // still includes bars pending removal) — so surviving bars start
  // widening/narrowing into their new share immediately, in step with the
  // departing/entering bars' own height animation, rather than waiting for
  // the departing bars to finish shrinking and get removed first. Found
  // 2026-09-22 checking this against the real transition: basing it on
  // renderCount instead left survivors flat for the first ~250ms, then
  // widening only after removal — sequential, not simultaneous.
  const targetColumnWidth =
    trackWidth !== null && total > 0 ? Math.max(0, (trackWidth - gapPx * (total - 1)) / total) : null;

  // Spotlight's bars fill most of their column, like the Design B mockup,
  // instead of the legacy 28-90px curve.
  const effectiveBarMaxWidth =
    spotlight && targetColumnWidth !== null ? Math.min(120, Math.max(4, targetColumnWidth * 0.75)) : barMaxWidth;

  return (
    <>
    <div className="hsab-calc-savings__chart">
      <div className="hsab-calc-savings__y-title" aria-hidden="true">
        <span>{Y_AXIS_TITLE}</span>
      </div>
      <div className="hsab-calc-savings__y-axis" aria-hidden="true">
        {labels.map((label, i) => (
          <span className="hsab-calc-savings__y-point" key={i} style={{ bottom: tickBottom(i, numPoints, 1) }}>
            {label}
          </span>
        ))}
        {/* invisible in-flow copy of the widest label so the axis keeps its width */}
        <span className="hsab-calc-savings__y-sizer">{labels[0]}</span>
      </div>
      <div className="hsab-calc-savings__x-axis" ref={trackRef}>
        {options.gridlineStyle && options.gridlineStyle !== 'none' && (
          // Mirrors __y-axis's own column-flex + space-between + matching
          // padding-bottom exactly, rather than computing line positions
          // from the label values independently — guarantees each line
          // lines up with its label pixel-for-pixel regardless of how that
          // distribution actually maps to bar-height percentages, instead
          // of risking two competing, possibly-inconsistent formulas.
          // Rendered first (behind the bars in paint order) with no
          // z-index, so bars cover the segments of each line they overlap.
          // The modifier class (--dotted/--dashed/--solid) is the one thing
          // that varies per style; add a new datasource option in
          // hsaSavingsCalc.yaml + a matching CSS rule to add another.
          <div className="hsab-calc-savings__gridlines" aria-hidden="true">
            {labels.map((_, i) => (
              <div className={`hsab-calc-savings__gridline hsab-calc-savings__gridline--${options.gridlineStyle}`} key={i} style={{ bottom: tickBottom(i, numPoints, 0) }} />
            ))}
          </div>
        )}
        {Array.from({ length: renderCount }, (_, i) => {
          const pendingRemoval = i >= total;
          const entering = !pendingRemoval && i >= enterFrom;
          const amt = pendingRemoval ? 0 : amounts[i];
          const collapsed = pendingRemoval || entering;
          const targetHeight = collapsed ? 0 : barHeightPx(amt, topValue);
          // Collapses to width 0 the same two frames height does (entering:
          // pops in narrow, widens once enterFrom flips; pendingRemoval:
          // narrows to nothing before actually leaving the DOM), so a bar
          // visually implodes on both axes together rather than just going
          // height-flat while still holding its old width for a moment.
          const columnWidth = collapsed ? 0 : targetColumnWidth;
          const isFinal = spotlight && i === total - 1;
          return (
            <div
              className="hsab-calc-savings__bar-container"
              key={i}
              // flex: none once we have a real measurement -- an explicit
              // width is what lets it transition, flex: 1's share would
              // otherwise still win/fight with it. Before the first
              // measurement (initial mount, one frame) fall back to flex: 1
              // so there's no flash of collapsed-width bars.
              style={columnWidth !== null ? { width: `${columnWidth}px`, flex: 'none' } : undefined}
            >
              {/*
                The tooltip is a *sibling* of the bar, not a child of it
                (Brad, 2026-09-22) — deliberately, after two rounds of
                chasing individual CSS properties that isolate the bar into
                its own stacking context and trap the tooltip's z-index
                inside it (first `transform` for centering, then `opacity`
                from the bar's own :hover feedback — both are on the CSS
                spec's stacking-context-creating list, and there's no way
                to enumerate every property a future hover/focus/active
                style might use). As a sibling anchored to the same
                position:relative bar-container, the tooltip's z-index now
                competes at the shared __x-axis level no matter what the
                bar itself does visually. Shown via the CSS sibling
                combinator (~) instead of a descendant selector — see
                styles.scss.
              */}
              <button
                type="button"
                className={'hsab-calc-savings__bar' + (isFinal ? ' hsab-calc-savings__bar--final' : '')}
                style={{ height: `${targetHeight}px`, maxWidth: `${effectiveBarMaxWidth}px` }}
                aria-label={`Year ${i + 1}: ${formatDollarPrecise(amt)}`}
                aria-hidden={pendingRemoval || undefined}
                tabIndex={pendingRemoval ? -1 : 0}
              />
              {/*
                bottom is set explicitly, tracking the bar's own top edge
                (the bar's reserve, calc(1.75rem + 7px), plus its current
                height) -- now that the tooltip's containing block is
                __bar-container instead of the bar itself, bottom: 100%
                would anchor it to the whole container's height (i.e. the
                same fixed spot for every bar) rather than to this specific
                bar's actual, animating top edge.
              */}
              {isFinal ? (
                // Permanent label, no hover tooltip needed. When the column
                // is narrower than the label (many bars, or a phone -- the
                // label grew with cents, 2026-10-01), it right-aligns to the
                // bar instead of centering (centered, it would overhang the
                // chart's right edge).
                <span
                  className={
                    'hsab-calc-savings__bar-value' +
                    (total > 10 || (targetColumnWidth ?? 0) < FINAL_LABEL_MIN_COLUMN_PX
                      ? ' hsab-calc-savings__bar-value--end'
                      : '')
                  }
                  style={{ bottom: `calc(1.75rem + 7px + ${targetHeight}px)` }}
                  aria-hidden="true"
                >
                  {formatDollarPrecise(amt)}
                </span>
              ) : (
                <span
                  className="hsab-calc-savings__bar-tooltip"
                  style={{ bottom: `calc(1.75rem + 7px + ${targetHeight}px)` }}
                  aria-hidden="true"
                >
                  {formatDollarPrecise(amt)}
                </span>
              )}
              <div className="hsab-calc-savings__bar-year">
                {!pendingRemoval && shouldShowYearLabel(i, total, targetColumnWidth !== null ? targetColumnWidth + gapPx : null) ? i + 1 : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
    <div className="hsab-calc-savings__x-title" aria-hidden="true">
      {X_AXIS_TITLE}
    </div>
    </>
  );
}
